/**
 * Canvas-based icon generation for map markers
 * Generates icons as data URLs for better performance than DOM-based icons
 */

/**
 * Helper to load and cache font for canvas text rendering
 */
let fontLoaded = false;
const loadFont = async () => {
  if (fontLoaded) return;
  
  try {
    // Check if Primary-Bold font is available
    await document.fonts.ready;
    fontLoaded = true;
  } catch (e) {
    console.warn('Font loading failed, using fallback');
    fontLoaded = true;
  }
};

/**
 * Generate a category icon with colored circle and optional number badge
 * 
 * @param {string} color - The category color (hex)
 * @param {number} numberOfEvents - Number to display (1-6, where 6 shows "5+")
 * @param {boolean} small - Whether to generate small icon (8x8) or normal (20x20)
 * @returns {string} Data URL of the generated icon
 */
export function generateCategoryIcon(color, numberOfEvents = 1, small = false) {
  const size = small ? 8 : 20;
  const canvas = document.createElement('canvas');
  
  // Use higher resolution for crisp rendering, then scale down
  const scale = 2;
  canvas.width = size * scale;
  canvas.height = size * scale;
  
  const ctx = canvas.getContext('2d', { alpha: true });
  ctx.scale(scale, scale);
  
  // Enable anti-aliasing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  if (small) {
    // Small icon: simple two-circle design
    // Outer white circle (border)
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(4, 4, 4, 0, 2 * Math.PI);
    ctx.fill();
    
    // Inner colored circle
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(4, 4, 3, 0, 2 * Math.PI);
    ctx.fill();
  } else {
    // Normal icon: three-circle design with optional number
    const centerX = 10;
    const centerY = 10;
    
    // Outer white circle (middle border)
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(centerX, centerY, 9, 0, 2 * Math.PI);
    ctx.fill();
    
    // Inner colored circle
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 8, 0, 2 * Math.PI);
    ctx.fill();
    
    // Draw number badge if > 1
    if (numberOfEvents > 1) {
      const text = numberOfEvents >= 6 ? '5+' : numberOfEvents.toString();
      const fontSize = numberOfEvents > 5 ? 10 : 14;
      
      // Set font
      ctx.font = `bold ${fontSize}px Primary-Bold, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'white';
      
      // Draw text shadow for better visibility
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;
      
      // Draw text
      ctx.fillText(text, centerX, centerY);
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }
  }
  
  return canvas.toDataURL('image/png');
}

/**
 * Generate the selected marker icon (theme-aware: black in light mode, white in dark mode)
 * 
 * @returns {string} Data URL of the selected icon
 */
export function generateSelectedIcon() {
  // Detect dark mode by checking body class (set by UIContext)
  const isDarkMode = document.body.classList.contains('dark');
  
  const innerColor = isDarkMode ? '#ffffff' : '#000000';
  const outerColor = isDarkMode ? '#000000' : '#ffffff';
  
  const size = 20;
  const canvas = document.createElement('canvas');
  const scale = 2;
  canvas.width = size * scale;
  canvas.height = size * scale;
  
  const ctx = canvas.getContext('2d', { alpha: true });
  ctx.scale(scale, scale);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  const centerX = 10;
  const centerY = 10;
  
  // Outer circle with subtle shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 1;
  
  ctx.fillStyle = outerColor;
  ctx.beginPath();
  ctx.arc(centerX, centerY, 10, 0, 2 * Math.PI);
  ctx.fill();
  
  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  
  // Inner circle (black in light mode, white in dark mode)
  ctx.fillStyle = innerColor;
  ctx.beginPath();
  ctx.arc(centerX, centerY, 7, 0, 2 * Math.PI);
  ctx.fill();
  
  return canvas.toDataURL('image/png');
}

/**
 * Generate the user location icon (pulsing dot)
 * Note: Pulsing animation is handled by CSS class
 * 
 * @returns {string} Data URL of the user location icon
 */
export function generateUserIcon() {
  const size = 18;
  const canvas = document.createElement('canvas');
  const scale = 2;
  canvas.width = size * scale;
  canvas.height = size * scale;
  
  const ctx = canvas.getContext('2d', { alpha: true });
  ctx.scale(scale, scale);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  const centerX = 9;
  const centerY = 9;
  
  // Add shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  
  // Main circle - use a blue color since we can't access CSS variable
  // The pulsing-dot class will handle the animation
  ctx.fillStyle = '#3B82F6'; // Tailwind blue-500
  ctx.beginPath();
  ctx.arc(centerX, centerY, 6, 0, 2 * Math.PI);
  ctx.fill();
  
  return canvas.toDataURL('image/png');
}

/**
 * Generate an icon with an image and optional event count badge
 * 
 * @param {string} imgSrc - Source URL of the image
 * @param {number} numberOfEvents - Number of events (shows badge if > 1)
 * @returns {Promise<string>} Data URL of the generated icon
 */
export async function generateImgIcon(imgSrc, numberOfEvents = 1) {
  return new Promise((resolve, reject) => {
    const size = 28;
    const canvas = document.createElement('canvas');
    const scale = 2;
    canvas.width = size * scale;
    canvas.height = size * scale;
    
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.scale(scale, scale);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    const centerX = 14;
    const centerY = 14;
    
    // Outer white circle
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(centerX, centerY, 13, 0, 2 * Math.PI);
    ctx.fill();
    
    // Load and draw image
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Handle CORS
    
    img.onload = () => {
      // Clip to circle
      ctx.save();
      ctx.beginPath();
      ctx.arc(centerX, centerY, 11, 0, 2 * Math.PI);
      ctx.clip();
      
      // Draw image
      ctx.globalAlpha = 0.9;
      ctx.drawImage(img, centerX - 11, centerY - 11, 22, 22);
      ctx.restore();
      
      // Draw event count badge if > 1
      if (numberOfEvents > 1) {
        const badgeX = centerX + 9;
        const badgeY = centerY - 7;
        const badgeRadius = 7.5;
        
        // Badge background (black circle)
        ctx.fillStyle = 'black';
        ctx.beginPath();
        ctx.arc(badgeX, badgeY, badgeRadius, 0, 2 * Math.PI);
        ctx.fill();
        
        // Badge text
        const text = numberOfEvents > 5 ? '5+' : numberOfEvents.toString();
        const fontSize = numberOfEvents > 5 ? 9 : 10;
        ctx.font = `600 ${fontSize}px Primary-Medium, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'white';
        ctx.fillText(text, badgeX, badgeY);
      }
      
      resolve(canvas.toDataURL('image/png'));
    };
    
    img.onerror = () => {
      // Fallback: generate a simple colored circle
      console.warn('Failed to load image for icon:', imgSrc);
      ctx.fillStyle = '#666';
      ctx.beginPath();
      ctx.arc(centerX, centerY, 11, 0, 2 * Math.PI);
      ctx.fill();
      resolve(canvas.toDataURL('image/png'));
    };
    
    img.src = imgSrc;
  });
}

// Pre-load font on module import
loadFont();

